import React, { useEffect, useRef } from 'react'
import { Type, Bold, Italic, Underline, List, ListOrdered, Image as ImageIcon, Sigma } from 'lucide-react'
import katex from 'katex'
import 'katex/dist/katex.min.css'
import { genId } from '../lib/id'
import { sanitizeInlineHtml } from '../lib/sanitizeHtml'
import { compressImageFile } from '../../../utils/imageCompress'
import type { RichTextCard } from '../types/LessonDoc'
import type { CardDef, CardEditorProps, CardPreviewProps } from './types'

const FONTS = ['Inter', 'Arial', 'Times New Roman', 'Georgia', 'Courier New']

// Render $...$ và $$...$$ thành KaTeX (đồng bộ cách của ContentRenderer).
function renderMath(html: string): string {
  let out = sanitizeInlineHtml(html || '')
  out = out.replace(/\$\$([\s\S]*?)\$\$/g, (_m, f) => {
    try { return katex.renderToString(String(f).trim(), { displayMode: true, throwOnError: false }) } catch { return f }
  })
  out = out.replace(/\$([^$\n]+?)\$/g, (_m, f) => {
    try { return katex.renderToString(String(f).trim(), { displayMode: false, throwOnError: false }) } catch { return f }
  })
  return out
}

const ToolbarBtn: React.FC<{ onClick: () => void; title: string; children: React.ReactNode }> = ({ onClick, title, children }) => (
  <button
    type="button"
    title={title}
    onMouseDown={(e) => e.preventDefault()} // giữ vùng chọn trong editor
    onClick={onClick}
    className="w-8 h-8 flex items-center justify-center rounded-md text-gray-600 hover:bg-gray-100"
  >
    {children}
  </button>
)

const Editor: React.FC<CardEditorProps> = ({ card, onChange }) => {
  const c = card as RichTextCard
  const ref = useRef<HTMLDivElement>(null)
  // HTML khởi tạo CỐ ĐỊNH (lúc mount). KHÔNG bind dangerouslySetInnerHTML vào c.html động:
  // nếu bind, mỗi phím gõ → React ghi lại innerHTML → con trỏ về đầu → "chữ ngược".
  // key={c.id} đã remount khi đổi thẻ nên ref này luôn = html của thẻ hiện tại lúc mount.
  const initialHtmlRef = useRef(c.html || '')

  useEffect(() => {
    try { document.execCommand('styleWithCSS', false, 'true') } catch { /* ignore */ }
  }, [])

  const sync = () => {
    if (ref.current) onChange({ html: ref.current.innerHTML })
  }
  const exec = (cmd: string, val?: string) => {
    ref.current?.focus()
    try { document.execCommand(cmd, false, val) } catch { /* ignore */ }
    sync()
  }

  const insertHTML = (html: string) => {
    ref.current?.focus()
    try { document.execCommand('insertHTML', false, html) } catch { /* ignore */ }
    sync()
  }

  const onPickImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const url = await compressImageFile(file)
    insertHTML(`<img src="${url}" style="max-width:100%;border-radius:8px;margin:8px 0" />`)
    e.target.value = ''
  }

  const insertFormula = () => {
    const latex = window.prompt('Nhập công thức LaTeX (vd: E = mc^2 hoặc \\frac{a}{b}):')
    if (latex) insertHTML(` $${latex}$ `)
  }

  return (
    <div className="space-y-2">
      {/* Thanh công cụ */}
      <div className="flex flex-wrap items-center gap-1 p-1.5 border border-gray-200 rounded-lg bg-gray-50">
        <select
          title="Phông chữ"
          onMouseDown={(e) => e.preventDefault()}
          onChange={(e) => exec('fontName', e.target.value)}
          className="h-8 text-xs border border-gray-200 rounded-md px-1 bg-white"
        >
          {FONTS.map((f) => <option key={f} value={f}>{f}</option>)}
        </select>
        <select
          title="Cỡ chữ"
          onMouseDown={(e) => e.preventDefault()}
          onChange={(e) => exec('fontSize', e.target.value)}
          className="h-8 text-xs border border-gray-200 rounded-md px-1 bg-white"
        >
          <option value="2">Nhỏ</option>
          <option value="3">Vừa</option>
          <option value="5">Lớn</option>
          <option value="6">Rất lớn</option>
        </select>
        <select
          title="Kiểu đoạn"
          onMouseDown={(e) => e.preventDefault()}
          onChange={(e) => exec('formatBlock', e.target.value)}
          className="h-8 text-xs border border-gray-200 rounded-md px-1 bg-white"
        >
          <option value="p">Đoạn</option>
          <option value="h2">Tiêu đề lớn</option>
          <option value="h3">Tiêu đề nhỏ</option>
        </select>
        <input
          type="color"
          title="Màu chữ"
          onMouseDown={(e) => e.preventDefault()}
          onChange={(e) => exec('foreColor', e.target.value)}
          className="w-8 h-8 rounded-md border border-gray-200 bg-white cursor-pointer"
        />
        <span className="w-px h-6 bg-gray-200 mx-0.5" />
        <ToolbarBtn title="Đậm" onClick={() => exec('bold')}><Bold size={15} /></ToolbarBtn>
        <ToolbarBtn title="Nghiêng" onClick={() => exec('italic')}><Italic size={15} /></ToolbarBtn>
        <ToolbarBtn title="Gạch chân" onClick={() => exec('underline')}><Underline size={15} /></ToolbarBtn>
        <ToolbarBtn title="Danh sách" onClick={() => exec('insertUnorderedList')}><List size={15} /></ToolbarBtn>
        <ToolbarBtn title="Danh sách số" onClick={() => exec('insertOrderedList')}><ListOrdered size={15} /></ToolbarBtn>
        <span className="w-px h-6 bg-gray-200 mx-0.5" />
        <ToolbarBtn title="Chèn công thức" onClick={insertFormula}><Sigma size={15} /></ToolbarBtn>
        <label title="Chèn ảnh" className="w-8 h-8 flex items-center justify-center rounded-md text-gray-600 hover:bg-gray-100 cursor-pointer">
          <ImageIcon size={15} />
          <input type="file" accept="image/*" className="hidden" onChange={onPickImage} />
        </label>
      </div>

      {/* Vùng soạn thảo (remount theo card.id để không nhảy con trỏ) */}
      <div
        key={c.id}
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={sync}
        className="min-h-[160px] p-3 border border-gray-200 rounded-lg text-sm leading-relaxed outline-none focus:ring-2 focus:ring-blue-200 prose prose-sm max-w-none bg-white"
        dangerouslySetInnerHTML={{ __html: initialHtmlRef.current }}
      />
      <p className="text-[11px] text-gray-400">Gõ công thức trong $...$ — sẽ hiển thị đẹp ở phần xem trước.</p>
    </div>
  )
}

const Preview: React.FC<CardPreviewProps> = ({ card }) => {
  const c = card as RichTextCard
  if (!c.html) {
    return <div className="p-4 rounded-lg bg-gray-50 border border-dashed border-gray-200 text-gray-400 text-sm text-center">Chưa có nội dung.</div>
  }
  return <div className="prose prose-sm max-w-none text-gray-800" dangerouslySetInnerHTML={{ __html: renderMath(c.html) }} />
}

export const richTextCardDef: CardDef = {
  type: 'richtext',
  label: 'Văn bản',
  description: 'Soạn thảo có định dạng: font, màu, cỡ chữ, công thức, ảnh',
  icon: Type,
  color: 'bg-teal-50 text-teal-600',
  createDefault: (): RichTextCard => ({ id: genId(), type: 'richtext', html: '' }),
  Editor,
  Preview,
}
